'use client'

/**
 * A banner above the app shell tells the shell how much of the viewport it has
 * taken, and the shell subtracts every one of them.
 *
 * THE DEFECT THIS EXISTS FOR (2026-09-21, rung 3 item 1). QJ gave the admin
 * add-on banner a published height and had the shells subtract it, which was
 * right and incomplete: the banner actually on screen in Tai's org is the
 * billing one, `PaymentMethodSetupBanner`, mounted by `BillingGate` above the
 * shell, and it published nothing. So `--admin-banner-h` computed to 0px, the
 * shells still claimed a whole viewport under a banner with real height, and
 * the document came out banner-height too tall: MEASURED at 39 to 112 pixels
 * of document scroll on the agent route, the root dashboard and /clients, with
 * the deck's slide rail under the fold again.
 *
 * ONE HOOK RATHER THAN A COPY PER BANNER, and one constant rather than a class
 * per shell. QJ's logic lived inside the admin banner, so the second banner
 * could only get it by being written again, which is how the first one came to
 * be the only one that had it. A third banner is now two edits, this hook and
 * one more term in `SHELL_HEIGHT_CLASS`, and nothing in any shell changes.
 *
 * MEASURED AND NOT ASSUMED, because these banners change height while they are
 * up: the admin one expands into per-addon rows and the billing one collapses
 * to a single line. A `ResizeObserver` is what keeps the number honest.
 *
 * THE WRAPPER IS `flow-root` ON PURPOSE. A banner carrying its own top margin
 * would have that margin collapse out of an ordinary wrapper, and the
 * published height would be short by exactly it. `flow-root` establishes a
 * block formatting context and changes nothing a broker can see.
 *
 * ZERO IS A STATEMENT, and it is written rather than left absent: a banner
 * that empties without unmounting would otherwise leave yesterday's height
 * behind. An absent variable and a `0px` one read the same in the calc, so the
 * fallback covers the page that never had a banner at all.
 *
 * A CALLBACK REF RATHER THAN AN OBJECT ONE, because the measurement has to
 * start when the node arrives and a ref assignment does not re-run an effect.
 * The callback's identity is stable, so React attaches it once rather than
 * detaching and re-attaching it on every render.
 */
import { useCallback, useEffect, useState } from 'react'

/**
 * The height every shell that wants a viewport asks for: the viewport minus
 * every banner that can sit above it.
 *
 * `dvh` rather than `vh` because mobile browser chrome makes the two differ by
 * the height of a toolbar. Each term falls back to 0px, which is the ordinary
 * case, so on a page with no banner at all this reads exactly as `100dvh`.
 *
 * A LITERAL STRING AND NOT A TEMPLATE, because Tailwind scans source text: a
 * class assembled at runtime is a class that never reaches the stylesheet.
 */
export const SHELL_HEIGHT_CLASS =
  'h-[calc(100dvh-var(--banner-h-admin,0px)-var(--banner-h-billing,0px)-var(--banner-h-lifecycle,0px)-var(--banner-h-cutover,0px)-var(--banner-h-shell-status,0px))]'

/**
 * Publish the calling banner's rendered height as `--banner-h-<name>`.
 *
 * Attach the returned ref to a `flow-root` wrapper around whatever the banner
 * paints. A banner that renders nothing attaches nothing, and the hook then
 * publishes `0px` instead of a stale height.
 */
export function usePublishBannerHeight(name: string) {
  const [node, setNode] = useState<HTMLDivElement | null>(null)
  const ref = useCallback((next: HTMLDivElement | null) => {
    // Set from the same value, so it settles: React bails out of a state
    // update whose value is unchanged, and the node is the same object across
    // the re-attachments a re-render can cause.
    setNode(next)
  }, [])

  useEffect(() => {
    // No `typeof document` guard: an effect never runs on the server, so the
    // one that was here was a branch nothing could reach (review round 1,
    // nitpick 2).
    const root = document.documentElement
    const property = `--banner-h-${name}`

    if (node === null) {
      root.style.setProperty(property, '0px')
      return () => root.style.removeProperty(property)
    }

    const write = () => {
      const height = Math.round(node.getBoundingClientRect().height)
      root.style.setProperty(property, `${height}px`)
    }
    write()
    // Feature-detected rather than assumed: jsdom ships no ResizeObserver, and
    // a hook that threw there would take every suite rendering a banner with
    // it.
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(write) : null
    observer?.observe(node)
    return () => {
      observer?.disconnect()
      root.style.removeProperty(property)
    }
  }, [name, node])

  return ref
}
